import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { UPI_APPS, BrandLogo } from "../../components/UpiApps";
import { routingPlan } from "../../lib/vpa";
import { Empty, useDebounced } from "../../components/sc/ui";
import { TablePager } from "../../components/sc/Pager";

const INK = "#14101F";
const VIOLET = "#4931E8";
const LIME = "#C7F23D";
const CREAM = "#F5F2EC";

function inr(n) {
  if (n == null) return "₹—";
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

function timeShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function PaymentLinks() {
  const { merchant, upiIds, routing } = useOutletContext();
  const [type, setType] = useState("fixed");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [customer, setCustomer] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [payMode, setPayMode] = useState("p2m"); // p2m | p2p | p2g
  const [p2gReady, setP2gReady] = useState(false); // gateway (Auto Pay) enabled for this merchant
  const [p2pApps, setP2pApps] = useState(["phonepe", "paytm"]); // for P2P
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [links, setLinks] = useState([]);
  const [filter, setFilter] = useState("all"); // all | active | paid | expired | disabled
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest"); // newest | oldest | amount_hi | amount_lo
  const [rowBusy, setRowBusy] = useState(""); // link id whose action is in flight
  const [copied, setCopied] = useState(""); // "url" | "intent" | ""
  const [copiedRow, setCopiedRow] = useState(""); // link id whose URL was just copied
  // Server-side paging: `links` holds only the current page; filter / search /
  // sort run on the server and `counts` carries the per-status totals.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, active: 0, paid: 0, expired: 0, disabled: 0 });
  const searchQ = useDebounced(search.trim(), 300);

  // Same plan the sidebar renders, so both always agree on where money lands.
  const plan = routingPlan(upiIds, merchant, routing);
  const modePlan = payMode === "p2p" ? plan.p2p : plan.p2m;
  const personalReady = plan.p2p.active > 0;
  const defaultVpa = modePlan.next || "—";
  const rotating = modePlan.live.length > 1;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payment-links", {
        params: { page, page_size: pageSize, status: filter, q: searchQ, sort },
      });
      const items = data.items || [];
      setLinks(items);
      setTotal(data.total || 0);
      if (data.counts) setCounts(data.counts);
      if (data.page && data.page !== page) setPage(data.page); // server clamped (e.g. last row deleted)
      setPreview((p) => p || items[0] || null);
    } catch { /* keep the current page on a transient error */ }
  }, [page, pageSize, filter, searchQ, sort]);

  useEffect(() => { load(); }, [load]);
  // Any filter / search / sort change starts again from page 1.
  useEffect(() => { setPage(1); }, [filter, searchQ, sort]);

  useEffect(() => {
    api.get("/gateway/profile").then(({ data }) => setP2gReady(!!(data.payin_enabled && data.gateway_live))).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const body = {
        amount_type: type, note: note.trim(), customer_name: customer.trim(),
        customer_phone: customerPhone.trim(),
        payment_mode: payMode,
        p2p_apps: payMode === "p2p" ? p2pApps : [],
      };
      if (type === "fixed") body.amount = parseInt(amount || "0", 10);
      const { data } = await api.post("/payment-links", body);
      setPreview(data);
      toast.success("Payment link generated — paisa incoming");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(""), 1400);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const copyRow = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRow(id);
      setTimeout(() => setCopiedRow(""), 1400);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  // Toggle a link between active/disabled. Paid/expired links are terminal and
  // can only be deleted (removed from the list), never re-enabled.
  const setLinkStatus = async (link, status) => {
    setRowBusy(link.id);
    try {
      const { data } = await api.patch(`/payment-links/${link.id}`, { status });
      setLinks((prev) => prev.map((l) => (l.id === link.id ? data : l)));
      if (preview?.id === link.id) setPreview(data);
      load(); // refresh status counts (and drop the row if it no longer matches the filter)
      toast.success(status === "disabled" ? "Link disabled" : "Link re-enabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setRowBusy("");
    }
  };

  const deleteLink = async (link) => {
    if (!window.confirm(`Delete link #${link.short_code}? This can't be undone.`)) return;
    setRowBusy(link.id);
    try {
      await api.delete(`/payment-links/${link.id}`);
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
      if (preview?.id === link.id) setPreview(null);
      load();
      toast.success("Link deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setRowBusy("");
    }
  };

  const stats = useMemo(() => ({
    active: counts.active || 0, paid: counts.paid || 0, expired: counts.expired || 0,
    disabled: counts.disabled || 0, total: counts.all || 0,
  }), [counts]);

  const previewAmount =
    type === "fixed" && amount
      ? `₹${parseInt(amount, 10).toLocaleString("en-IN")}`
      : type === "open"
      ? "Customer picks"
      : "₹ —";

  const livePreview = preview || {
    upi_intent: `upi://pay?pa=${encodeURIComponent(defaultVpa)}&pn=${encodeURIComponent(
      merchant?.name || "Merchant"
    )}&am=${amount || ""}&cu=INR&tn=${encodeURIComponent(note || "")}`,
    short_url: "—",
    qr_data_url: null,
  };

  const shareText = preview ? `Pay here: ${preview.short_url}` : "";

  return (
    <div className="ui-page pl-page" data-testid="links-page">
      {/* TOP — quick stats strip */}
      <div className="ui-grid ui-grid-3 pl-stats">
        {[
          { l: "Active links", v: stats.active, tone: "violet" },
          { l: "Paid", v: stats.paid, tone: "mint" },
          { l: "Total created", v: stats.total, tone: "ink" },
        ].map((s) => (
          <div
            key={s.l}
            className="ui-card ui-stat pl-stat"
            data-testid={`pl-stat-${s.l.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div style={{ minWidth: 0 }}>
              <span className="ui-eyebrow">{s.l}</span>
              <div className="ui-stat-value pl-stat-value">{s.v}</div>
            </div>
            <span className={`pl-stat-icon is-${s.tone}`} aria-hidden="true">
              {s.tone === "violet" ? "🔗" : s.tone === "mint" ? "✓" : "Σ"}
            </span>
          </div>
        ))}
      </div>

      {/* GENERATOR + PREVIEW */}
      <div className="pl-grid">
        {/* form */}
        <section className="ui-card pl-form" data-testid="link-form">
          <header className="ui-card-head">
            <div className="ui-row" style={{ flexWrap: "nowrap", minWidth: 0 }}>
              <span className="pl-form-badge" aria-hidden="true">+</span>
              <div style={{ minWidth: 0 }}>
                <h3 className="ui-card-title">New payment link</h3>
                <p className="ui-card-sub">Share a UPI link or QR — money lands straight in your UPI ID.</p>
              </div>
            </div>
          </header>

          <div className="ui-card-pad pl-form-body">
            <div className="ui-field">
              <span>Amount type</span>
              <div className="pl-seg">
                <button
                  data-testid="link-type-fixed"
                  onClick={() => setType("fixed")}
                  type="button"
                  aria-pressed={type === "fixed"}
                  className={`pl-seg-btn ${type === "fixed" ? "is-on" : ""}`}
                >
                  <span className="pl-seg-title">Fixed amount</span>
                </button>
                <button
                  data-testid="link-type-open"
                  onClick={() => setType("open")}
                  type="button"
                  aria-pressed={type === "open"}
                  className={`pl-seg-btn ${type === "open" ? "is-on" : ""}`}
                >
                  <span className="pl-seg-title">Customer decides</span>
                </button>
              </div>
            </div>

            {type === "fixed" && (
              <label className="ui-field">
                <span>Amount (₹)</span>
                <div className="pl-amount">
                  <span className="pl-amount-sym" aria-hidden="true">₹</span>
                  <input
                    data-testid="link-amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
                    inputMode="numeric"
                    placeholder="2499"
                    className="ui-input pl-amount-input"
                  />
                </div>
              </label>
            )}

            <label className="ui-field">
              <span>Purpose / note</span>
              <input
                data-testid="link-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Order #1234"
                className="ui-input"
              />
            </label>

            <div>
              <div className="ui-grid ui-grid-2 pl-cust-grid">
                <label className="ui-field">
                  <span>
                    Customer name <span className="pl-opt">· optional</span>
                  </span>
                  <input
                    data-testid="link-customer"
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    placeholder="Rohan Mehta"
                    className="ui-input"
                  />
                </label>
                <label className="ui-field">
                  <span>
                    Phone <span className="pl-opt">· optional</span>
                  </span>
                  <input
                    data-testid="link-customer-phone"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value.replace(/[^0-9+ -]/g, "").slice(0, 20))}
                    inputMode="tel"
                    placeholder="+91 98765 43210"
                    className="ui-input is-mono"
                  />
                </label>
              </div>
              <p className="pl-hint">
                Captured on the link so you can tell exactly who paid — shown in Deposits &amp; the confirmation queue even when the bank notification carries no name.
              </p>
            </div>

            {/* PAYMENT MODE */}
            <div className="ui-field">
              <span>Payment mode</span>
              <div className="pl-seg">
                {[
                  ["p2m", "Merchant (P2M)", "Any UPI app"],
                  ["p2p", "Personal (P2P)", "PhonePe / Paytm"],
                  ...(p2gReady ? [["p2g", "Gateway (P2G)", "Auto-confirmed"]] : []),
                ].map(([k, label, sub]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`link-mode-${k}`}
                    onClick={() => setPayMode(k)}
                    aria-pressed={payMode === k}
                    className={`pl-seg-btn is-stack ${payMode === k ? "is-on" : ""}`}
                  >
                    <span className="pl-seg-title">{label}</span>
                    <span className="pl-seg-sub">{sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {payMode === "p2p" && (
              <div className="ui-field">
                <span>Accept via</span>
                <div className="ui-row" style={{ gap: 8 }}>
                  {UPI_APPS.map((app) => {
                    const on = p2pApps.includes(app.key);
                    return (
                      <button
                        key={app.key}
                        type="button"
                        data-testid={`link-app-${app.key}`}
                        onClick={() => setP2pApps((prev) => (prev.includes(app.key) ? prev.filter((k) => k !== app.key) : [...prev, app.key]))}
                        aria-pressed={on}
                        className={`pl-app ${on ? "is-on" : ""}`}
                      >
                        <span className="pl-app-check" aria-hidden="true">{on ? "✓" : ""}</span>
                        <BrandLogo app={app.key} height={17} />
                      </button>
                    );
                  })}
                </div>
                <small style={{ color: personalReady ? undefined : "#b3261e", lineHeight: 1.45 }}>
                  {personalReady
                    ? "Opens PhonePe/Paytm to send to your personal UPI. Amount is locked."
                    : plan.p2p.status === "disabled"
                      ? "All your Personal UPI IDs are switched off — enable one in My Account to take P2P payments."
                      : "Add a Personal UPI ID in My Account first — P2P settles into your personal account."}
                </small>
              </div>
            )}

            <button data-testid="link-generate" onClick={generate} disabled={busy} className="ui-btn ui-btn-primary pl-generate">
              {busy ? "Cooking up the link…" : "Generate link →"}
            </button>

            {payMode === "p2g" ? (
              <div className="ui-banner is-info pl-settle" data-testid="settle-note">
                <span aria-hidden="true">⚡</span>
                <div style={{ minWidth: 0 }}>
                  Paid on the SuperCash page and confirmed automatically by the gateway — the amount lands in your <strong>SuperCash balance</strong> (see Auto Pay / Payout).
                </div>
              </div>
            ) : modePlan.active ? (
              <div className="ui-banner is-info pl-settle" data-testid="settle-note">
                <span aria-hidden="true">₹</span>
                <div style={{ minWidth: 0 }}>
                  Funds settle to <strong className="ui-mono pl-break">{defaultVpa}</strong>
                  {rotating ? <> — rotating across <strong>{modePlan.live.length}</strong> UPI IDs</> : null}{modePlan.standby.length ? <> (+{modePlan.standby.length} standby)</> : null} the moment the customer approves.
                </div>
              </div>
            ) : (
              <div className="ui-banner is-danger pl-settle" style={{ color: "#b3261e", borderColor: "#b3261e" }} data-testid="settle-note-none">
                <span aria-hidden="true">!</span>
                <div style={{ minWidth: 0 }}>
                  No active UPI ID — enable one in <strong>My Account</strong> before generating a link.
                </div>
              </div>
            )}
          </div>
        </section>

        {/* live preview */}
        <section className="pl-preview" data-testid="link-preview">
          <div className="ui-between pl-preview-head">
            <span className="ui-row" style={{ gap: 9 }}>
              <span className="pl-live-dot" />
              <span className="pl-eyebrow" style={{ color: LIME }}>Live preview</span>
            </span>
            <span className="ui-mono pl-dim" style={{ fontSize: 11 }}>{preview ? `#${preview.short_code}` : "—"}</span>
          </div>

          {/* amount + QR */}
          <div className="pl-prevtop">
            <div style={{ minWidth: 0 }}>
              <div className="pl-eyebrow" style={{ marginBottom: 6 }}>{merchant?.name || "Merchant"} collects</div>
              <div className="pl-prev-amount">{previewAmount}</div>
              <div className="pl-prev-note">{preview?.note ?? note ?? "—"}</div>
              {(preview?.customer_name || preview?.customer_phone) && (
                <div className="pl-prev-payer" data-testid="preview-customer">
                  <span className="pl-dim">Payer:</span>
                  <span style={{ fontWeight: 700 }}>{preview.customer_name || "—"}</span>
                  {preview.customer_phone && <span style={{ color: "#b6b0c8" }}>· {preview.customer_phone}</span>}
                </div>
              )}
              <div className="pl-prev-vpa" data-testid="preview-settle-vpa">
                <span className="pl-prev-vpa-ico" aria-hidden="true">₹</span>
                <span className="pl-break" style={{ minWidth: 0 }}>
                  {preview?.vpa || defaultVpa}{!preview && rotating ? ` +${modePlan.live.length - 1}` : ""}
                </span>
              </div>
            </div>
            <div className="pl-qr">
              {livePreview.qr_data_url ? (
                <img src={livePreview.qr_data_url} alt="UPI QR" data-testid="link-qr" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
              ) : (
                <div className="pl-qr-empty">Generate to see QR</div>
              )}
            </div>
          </div>

          {/* payment mode + P2P apps */}
          <div style={{ marginTop: 20 }}>
            <div className="pl-eyebrow" style={{ marginBottom: 8 }}>Payment mode</div>
            {(() => {
              const mode = preview ? (preview.payment_mode || "p2m") : payMode;
              const keys = preview ? (preview.p2p_apps || []) : p2pApps;
              if (mode === "p2g") {
                return <div className="ui-mono" style={{ fontSize: 12, color: "#b6b0c8" }}>Gateway (P2G) · any UPI app · auto-confirmed</div>;
              }
              if (mode !== "p2p") {
                return <div className="ui-mono" style={{ fontSize: 12, color: "#b6b0c8" }}>Merchant (P2M) · every UPI app</div>;
              }
              return (
                <div className="ui-row" style={{ gap: 8 }} data-testid="preview-apps">
                  <span className="ui-mono" style={{ fontSize: 12, color: "#b6b0c8" }}>Personal (P2P) ·</span>
                  {(keys.length ? keys : ["phonepe", "paytm"]).map((k) => (
                    <span key={k} className="pl-prev-app">
                      <BrandLogo app={k} height={16} />
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* divider */}
          <div className="pl-divider" />

          {/* short url + intent */}
          <CopyRow
            label="Shareable link"
            value={livePreview.short_url}
            disabled={!preview}
            copied={copied === "url"}
            onCopy={() => copy(livePreview.short_url, "url")}
            testid="link-copy-url"
            valueTestid="link-short-url"
            accent={LIME}
          />
          <div style={{ height: 12 }} />
          <CopyRow
            label="UPI intent"
            value={livePreview.upi_intent}
            copied={copied === "intent"}
            onCopy={() => copy(livePreview.upi_intent, "intent")}
            testid="link-copy-intent"
            valueTestid="link-upi-intent"
            accent="#9fd4ff"
          />

          {/* share row */}
          <div style={{ marginTop: 20 }}>
            <div className="pl-eyebrow" style={{ marginBottom: 8 }}>Share</div>
            <div className="pl-share">
              <ShareChip icon="💬" label="WhatsApp" bg="#1FBF75" fg="#0a3320" border="#0a3320" url={preview ? `https://wa.me/?text=${encodeURIComponent(shareText)}` : ""} />
              <ShareChip icon="✉" label="Email" bg="#fff" fg={INK} border={INK} url={preview ? `mailto:?subject=${encodeURIComponent("Payment request")}&body=${encodeURIComponent(shareText)}` : ""} />
              <ShareChip icon="✆" label="SMS" bg="#221d36" fg="#fff" border="#3a3550" url={preview ? `sms:?&body=${encodeURIComponent(shareText)}` : ""} />
            </div>
          </div>
        </section>
      </div>

      {/* LIST */}
      <section className="ui-card" data-testid="links-table">
        <header className="ui-card-head">
          <div style={{ minWidth: 0 }}>
            <h3 className="ui-card-title">Your links</h3>
            <p className="ui-card-sub">Tap a link to load it into the preview.</p>
          </div>
          <div className="ui-toolbar pl-tools">
            <div className="pl-search">
              <span className="pl-search-ico" aria-hidden="true">⌕</span>
              <input
                data-testid="links-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code, note, customer…"
                className="ui-input is-mono"
              />
            </div>
            <select
              data-testid="links-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="ui-select"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="amount_hi">Amount ↓</option>
              <option value="amount_lo">Amount ↑</option>
            </select>
          </div>
        </header>

        <div className="pl-filters">
          <div className="ui-tabs" role="tablist">
            {[
              ["all", "All", stats.total],
              ["active", "Active", stats.active],
              ["paid", "Paid", stats.paid],
              ["expired", "Expired", stats.expired],
              ["disabled", "Disabled", stats.disabled],
            ].map(([k, label, n]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={filter === k}
                data-testid={`links-filter-${k}`}
                onClick={() => setFilter(k)}
                className={`ui-tab ${filter === k ? "is-active" : ""}`}
              >
                {label}
                <span className="ui-count">{n}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="ui-table-wrap sc-scroll">
          {links.length === 0 ? (
            <EmptyState filtered={stats.total > 0} />
          ) : (
            <table className="ui-table pl-table">
              <thead>
                <tr>
                  <th>Link</th>
                  <th>Customer</th>
                  <th className="pl-col-mode">Mode</th>
                  <th className="is-num">Amount</th>
                  <th>Status</th>
                  <th className="pl-col-created">Created</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => {
                  const selected = preview?.id === l.id;
                  const isP2p = (l.payment_mode || "p2m") === "p2p";
                  const apps = isP2p ? (l.p2p_apps && l.p2p_apps.length ? l.p2p_apps : ["phonepe", "paytm"]) : [];
                  return (
                    <tr
                      key={l.id}
                      onClick={() => setPreview(l)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPreview(l); }}
                      data-testid={`link-row-${l.short_code}`}
                      className={`is-click pl-row ${selected ? "is-active" : ""}`}
                    >
                      <td data-label="" className="pl-cell-link">
                        <div className="pl-link">
                          <span className="pl-link-ico" aria-hidden="true">#</span>
                          <span style={{ minWidth: 0, flex: 1 }}>
                            <span className="ui-trunc ui-mono pl-link-url">{l.short_url.replace(/^https?:\/\//, "")}</span>
                            {l.note ? <span className="ui-cell-sub" style={{ fontFamily: "inherit" }}>{l.note}</span> : null}
                          </span>
                        </div>
                      </td>
                      <td data-label="Customer">
                        <div className="pl-cust">
                          <span className="ui-trunc" style={{ color: "#51495f" }}>{l.customer_name || "—"}</span>
                          {l.customer_phone ? <span className="ui-cell-sub">{l.customer_phone}</span> : null}
                        </div>
                      </td>
                      <td data-label="Mode" className="pl-col-mode">
                        <span className="pl-mode">
                          {isP2p ? apps.map((k) => <BrandLogo key={k} app={k} height={12} />) : <span className="ui-mono ui-faint" style={{ fontSize: 11 }}>{(l.payment_mode || "p2m").toUpperCase()}</span>}
                        </span>
                      </td>
                      <td data-label="Amount" className="is-num">
                        {l.amount_type === "fixed" ? (
                          <span className="pl-amt">{inr(l.amount)}</span>
                        ) : (
                          <span className="ui-mono" style={{ fontSize: 12, fontWeight: 700, color: "#7a7488" }}>OPEN</span>
                        )}
                      </td>
                      <td data-label="Status">
                        <span className={`sc-status sc-status-${l.status}`}>{l.status}</span>
                      </td>
                      <td data-label="Created" className="pl-col-created">
                        <span className="ui-mono ui-muted" style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>{timeShort(l.created_at)}</span>
                      </td>
                      <td data-label="" className="pl-cell-actions">
                        <span className="pl-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            data-testid={`link-row-copy-${l.short_code}`}
                            onClick={() => copyRow(l.short_url, l.id)}
                            title="Copy link"
                            className={`pl-act pl-act-copy ${copiedRow === l.id ? "is-done" : ""}`}
                          >
                            {copiedRow === l.id ? "✓" : "copy"}
                          </button>
                          {l.status === "active" && (
                            <button
                              type="button"
                              data-testid={`link-row-disable-${l.short_code}`}
                              onClick={() => setLinkStatus(l, "disabled")}
                              disabled={rowBusy === l.id}
                              title="Disable link"
                              aria-label="Disable link"
                              className="pl-act"
                            >⏸</button>
                          )}
                          {l.status === "disabled" && (
                            <button
                              type="button"
                              data-testid={`link-row-enable-${l.short_code}`}
                              onClick={() => setLinkStatus(l, "active")}
                              disabled={rowBusy === l.id}
                              title="Re-enable link"
                              aria-label="Re-enable link"
                              className="pl-act is-go"
                            >▶</button>
                          )}
                          <button
                            type="button"
                            data-testid={`link-row-delete-${l.short_code}`}
                            onClick={() => deleteLink(l)}
                            disabled={rowBusy === l.id}
                            title="Delete link"
                            aria-label="Delete link"
                            className="pl-act is-danger"
                          >🗑</button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <TablePager testId="links-pager" page={page} total={total} size={pageSize} onPage={setPage} onSize={setPageSize} />
      </section>

      <style>{`
        .pl-page { min-width: 0; }
        .pl-break { word-break: break-all; overflow-wrap: anywhere; }

        /* stats */
        .pl-stat { flex-direction: row; align-items: center; justify-content: space-between; gap: 12px; }
        .pl-stat-value { margin-top: 2px; font-size: 26px; }
        .pl-stat-icon { flex: none; width: 40px; height: 40px; border-radius: 12px; border: 2px solid ${INK}; display: grid; place-items: center; font-size: 17px; }
        .pl-stat-icon.is-violet { background: #ECE8FB; color: ${VIOLET}; }
        .pl-stat-icon.is-mint { background: #DFF7E7; color: #137a47; }
        .pl-stat-icon.is-ink { background: ${INK}; color: #fff; }

        /* generator + preview layout */
        .pl-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr); gap: 18px; align-items: start; }
        .pl-form-badge { flex: none; width: 34px; height: 34px; border-radius: 10px; background: ${VIOLET}; color: #fff; border: 2px solid ${INK}; display: grid; place-items: center; font-family: "Inter", sans-serif; font-weight: 800; font-size: 18px; }
        .pl-form-body { display: flex; flex-direction: column; gap: 18px; }
        .pl-opt { color: #9a93a8; text-transform: none; letter-spacing: 0; font-weight: 400; }
        .pl-hint { margin: 8px 0 0; font-size: 11.5px; color: #7a7488; line-height: 1.45; }
        .pl-cust-grid { gap: 12px; }

        /* segmented choice buttons */
        .pl-seg { display: grid; grid-template-columns: repeat(auto-fit, minmax(128px, 1fr)); gap: 10px; }
        .pl-seg-btn { min-height: 46px; padding: 10px 14px; border: 2.5px solid ${INK}; border-radius: 12px; background: #fff; color: ${INK}; cursor: pointer; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; text-align: center; font-family: "Inter", sans-serif; transition: transform .08s, box-shadow .08s, background .12s; min-width: 0; }
        .pl-seg-btn.is-stack { align-items: flex-start; text-align: left; }
        .pl-seg-btn:hover { background: #FBFAF7; }
        .pl-seg-btn.is-on { background: ${INK}; color: #fff; box-shadow: 4px 4px 0 ${VIOLET}; transform: translate(-1px, -1px); }
        .pl-seg-title { font-size: 14px; font-weight: 700; line-height: 1.2; }
        .pl-seg-sub { font-family: "JetBrains Mono", monospace; font-size: 10.5px; opacity: .7; }

        /* amount */
        .pl-amount { position: relative; }
        .pl-amount-sym { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); font-family: "Inter", sans-serif; font-weight: 800; font-size: 22px; color: #9a93a8; pointer-events: none; }
        .pl-amount-input { min-height: 52px; padding-left: 38px; font-family: "Inter", sans-serif; font-size: 22px; font-weight: 700; }

        /* p2p app chips */
        .pl-app { display: inline-flex; align-items: center; gap: 8px; min-height: 38px; padding: 7px 13px; border-radius: 100px; border: 2px solid ${INK}; background: #fff; cursor: pointer; transition: box-shadow .1s; }
        .pl-app.is-on { border-color: ${VIOLET}; box-shadow: 2px 2px 0 ${VIOLET}; }
        .pl-app-check { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #c9c3d6; background: #fff; display: grid; place-items: center; color: #fff; font-size: 9px; font-weight: 800; }
        .pl-app.is-on .pl-app-check { border-color: ${VIOLET}; background: ${VIOLET}; }

        .pl-generate { width: 100%; min-height: 50px; font-size: 16px; }
        .pl-settle { font-size: 12.5px; line-height: 1.45; padding: 11px 14px; }
        .pl-settle > span { font-family: "JetBrains Mono", monospace; font-weight: 700; }

        /* dark live-preview panel */
        .pl-preview { background: ${INK}; color: #fff; border: 2.5px solid ${INK}; border-radius: 20px; box-shadow: 6px 6px 0 ${VIOLET}; padding: 24px; min-width: 0; }
        .pl-preview-head { margin-bottom: 20px; flex-wrap: nowrap; }
        .pl-eyebrow { font-family: "JetBrains Mono", monospace; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #8a84a0; }
        .pl-dim { color: #8a84a0; }
        .pl-live-dot { width: 8px; height: 8px; border-radius: 50%; background: #1FBF75; display: inline-block; box-shadow: 0 0 0 4px rgba(31,191,117,.18); }
        .pl-prevtop { display: grid; grid-template-columns: minmax(0, 1fr) 168px; gap: 22px; align-items: center; }
        .pl-prev-amount { font-family: "Inter", sans-serif; font-weight: 800; font-size: 44px; letter-spacing: -0.02em; line-height: 1; overflow-wrap: anywhere; }
        .pl-prev-note { font-size: 13.5px; color: #b6b0c8; margin-top: 10px; min-height: 18px; overflow-wrap: anywhere; }
        .pl-prev-payer { margin-top: 8px; font-family: "JetBrains Mono", monospace; font-size: 12px; color: #e9e5f7; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .pl-prev-vpa { display: flex; align-items: flex-start; gap: 7px; margin-top: 12px; font-family: "JetBrains Mono", monospace; font-size: 12px; color: ${LIME}; min-width: 0; }
        .pl-prev-vpa-ico { flex: none; width: 16px; height: 16px; border-radius: 4px; background: ${LIME}; border: 2px solid ${INK}; color: ${INK}; display: grid; place-items: center; font-size: 9px; font-weight: 800; }
        .pl-qr { background: #fff; border: 2.5px solid ${INK}; border-radius: 16px; padding: 10px; display: grid; place-items: center; aspect-ratio: 1 / 1; width: 100%; max-width: 168px; box-sizing: border-box; box-shadow: 4px 4px 0 ${LIME}; }
        .pl-qr-empty { width: 100%; height: 100%; background: ${CREAM}; display: grid; place-items: center; font-family: "JetBrains Mono", monospace; font-size: 10px; color: #7a7488; border-radius: 8px; text-align: center; padding: 8px; box-sizing: border-box; }
        .pl-prev-app { display: inline-flex; align-items: center; background: #fff; border: 2px solid #3a3550; border-radius: 100px; padding: 5px 11px; }
        .pl-divider { height: 2px; background: #2a2540; border-radius: 2px; margin: 22px 0; }
        .pl-copy-val { flex: 1; min-width: 0; min-height: 40px; display: flex; align-items: center; background: #221d36; border: 2px solid #3a3550; border-radius: 10px; padding: 9px 13px; font-family: "JetBrains Mono", monospace; font-size: 12.5px; white-space: nowrap; overflow-x: auto; box-sizing: border-box; }
        .pl-copy-btn { flex: none; min-height: 40px; min-width: 72px; padding: 0 14px; border: 2px solid ${INK}; border-radius: 10px; color: ${INK}; font-family: "JetBrains Mono", monospace; font-size: 11px; font-weight: 700; transition: background .15s, transform .08s; }
        .pl-copy-btn:active:not(:disabled) { transform: translate(1px, 1px); }
        .pl-share { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
        .pl-chip { display: flex; align-items: center; justify-content: center; gap: 7px; min-height: 42px; border-radius: 100px; font-family: "Inter", sans-serif; font-weight: 700; font-size: 13px; padding: 8px; white-space: nowrap; box-sizing: border-box; transition: transform .08s; }
        a:hover > .pl-chip { transform: translate(-1px, -1px); }

        /* links list */
        .pl-tools { flex: 1 1 320px; justify-content: flex-end; }
        .pl-search { position: relative; flex: 0 1 260px; min-width: 0; }
        .pl-search .ui-input { padding-left: 28px; }
        .pl-search-ico { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); font-size: 13px; color: #9a93a8; pointer-events: none; }
        .pl-filters { padding: 14px 20px 6px; }
        .pl-filters .ui-tabs { box-shadow: none; width: max-content; }
        .pl-filters .ui-tab { min-height: 34px; }
        .pl-table td { max-width: none; }
        .pl-cell-link { max-width: 300px !important; }
        .pl-row.is-active td:first-child { box-shadow: inset 3px 0 0 ${VIOLET}; }
        .pl-row:focus-visible { outline: 2.5px solid ${VIOLET}; outline-offset: -2.5px; }
        .pl-link { display: flex; align-items: center; gap: 9px; min-width: 0; max-width: 300px; }
        .pl-link-ico { flex: none; width: 28px; height: 28px; border-radius: 8px; background: #ECE8FB; color: ${VIOLET}; border: 2px solid ${VIOLET}; display: grid; place-items: center; font-family: "JetBrains Mono", monospace; font-size: 11px; font-weight: 700; }
        .pl-link-url { font-weight: 700; color: ${VIOLET}; }
        .pl-cust { min-width: 0; max-width: 180px; }
        .pl-mode { display: inline-flex; flex-direction: column; align-items: flex-start; gap: 4px; }
        .pl-amt { font-family: "Inter", sans-serif; font-weight: 700; white-space: nowrap; }
        .pl-actions { display: inline-flex; gap: 6px; justify-content: flex-end; align-items: center; }
        .pl-cell-actions { text-align: right; white-space: nowrap; }
        .pl-act { min-width: 36px; height: 36px; padding: 0 9px; cursor: pointer; background: #fff; border: 2px solid ${INK}; border-radius: 9px; font-size: 12px; color: ${INK}; line-height: 1; display: inline-grid; place-items: center; transition: transform .08s, background .12s; }
        .pl-act:hover:not(:disabled) { transform: translate(-1px, -1px); box-shadow: 2px 2px 0 ${INK}; }
        .pl-act:disabled { opacity: .5; cursor: not-allowed; }
        .pl-act-copy { font-family: "JetBrains Mono", monospace; font-size: 10.5px; font-weight: 700; }
        .pl-act.is-done { background: ${LIME}; }
        .pl-act.is-go { background: #DFF7E7; }
        .pl-act.is-danger { border-color: #b3261e; color: #b3261e; }
        .pl-act.is-danger:hover:not(:disabled) { box-shadow: 2px 2px 0 #b3261e; }
        [data-testid="links-table"] .sc-status-expired { background: #F1ECE0; color: #6a6478; border-color: #6a6478; }
        [data-testid="links-table"] .sc-status-disabled { background: #FBE9E7; color: #9c1f16; border-color: #9c1f16; }

        @media (max-width: 1100px) and (min-width: 721px) {
          .pl-col-mode { display: none; }
        }
        @media (max-width: 860px) and (min-width: 721px) {
          .pl-col-created { display: none; }
        }
        @media (max-width: 980px) {
          .pl-grid { grid-template-columns: minmax(0, 1fr); }
        }
        @media (max-width: 720px) {
          .pl-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
          .pl-stat { flex-direction: column-reverse; align-items: flex-start; gap: 8px; padding: 12px; }
          .pl-stat-icon { width: 30px; height: 30px; font-size: 13px; border-radius: 9px; }
          .pl-stat-value { font-size: 22px; }
          .pl-stat .ui-eyebrow { font-size: 9.5px; }
          .pl-tools { flex-basis: 100%; justify-content: stretch; }
          .pl-search { flex: 1 1 100%; }
          .pl-tools .ui-select { flex: 1 1 100%; width: 100%; }
          .pl-filters { padding: 12px 15px 4px; }
          .pl-table tr.pl-row { padding: 12px 15px; }
          .pl-row.is-active td:first-child { box-shadow: none; }
          .pl-table tr.pl-row.is-active { box-shadow: inset 4px 0 0 ${VIOLET}; background: #F4F1FD; }
          .pl-table tr.pl-row.is-active td { background: transparent; }
          .pl-cell-link { max-width: none !important; padding-bottom: 8px !important; }
          .pl-cell-link .pl-link { max-width: none; width: 100%; text-align: left; }
          .pl-cust { max-width: 60vw; text-align: right; }
          .pl-cust .ui-trunc { max-width: 60vw; }
          .pl-mode { flex-direction: row; flex-wrap: wrap; justify-content: flex-end; }
          .pl-cell-actions { justify-content: flex-end !important; padding-top: 10px !important; }
          .pl-actions { gap: 8px; }
          .pl-act { min-width: 40px; height: 40px; }
        }
        @media (max-width: 640px) {
          .pl-cust-grid { grid-template-columns: minmax(0, 1fr); }
          .pl-prevtop { grid-template-columns: minmax(0, 1fr); }
          .pl-qr { max-width: 220px; justify-self: center; order: -1; }
          .pl-prev-amount { font-size: 38px; }
        }
        @media (max-width: 480px) {
          .pl-stats { gap: 8px; }
          .pl-stat { padding: 10px; }
          .pl-stat-value { font-size: 20px; }
          .pl-preview { padding: 18px 16px; border-radius: 18px; box-shadow: 4px 4px 0 ${VIOLET}; }
          .pl-prev-amount { font-size: 34px; }
          .pl-seg { gap: 8px; }
          .pl-seg-btn { padding: 10px; }
          .pl-seg-title { font-size: 13px; }
          .pl-chip { font-size: 12px; gap: 5px; padding: 8px 4px; }
        }
        @media (max-width: 360px) {
          .pl-stats { grid-template-columns: minmax(0, 1fr); }
          .pl-stat { flex-direction: row; align-items: center; justify-content: space-between; }
          .pl-seg { grid-template-columns: minmax(0, 1fr); }
          .pl-share { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>
    </div>
  );
}

function CopyRow({ label, value, copied, onCopy, disabled, testid, valueTestid, accent }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="pl-eyebrow" style={{ marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
        <div
          data-testid={valueTestid}
          className="sc-scroll pl-copy-val"
          style={{ color: accent || "#fff" }}
        >
          {value}
        </div>
        <button
          type="button"
          data-testid={testid}
          onClick={onCopy}
          disabled={disabled}
          className="pl-copy-btn"
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            background: copied ? LIME : "#fff",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {copied ? "copied ✦" : "copy"}
        </button>
      </div>
    </div>
  );
}

function ShareChip({ icon, label, bg, fg, border, url }) {
  const disabled = !url;
  const content = (
    <span
      className="pl-chip"
      style={{
        background: bg,
        color: fg,
        border: `2px solid ${border}`,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span style={{ fontSize: 13 }} aria-hidden="true">{icon}</span>
      {label}
    </span>
  );
  if (disabled) return <div data-testid={`share-${label.toLowerCase()}`}>{content}</div>;
  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", minWidth: 0 }} data-testid={`share-${label.toLowerCase()}`}>
      {content}
    </a>
  );
}

function EmptyState({ filtered }) {
  if (filtered) {
    return <Empty icon="🔎" title="No matching links">Try another status tab or search.</Empty>;
  }
  return (
    <Empty icon="🔗" title="No links yet">
      Generate one above — takes about 3 seconds.
    </Empty>
  );
}
